import { defaultAddStatus, defaultAddOwnership } from "../utils/settings.js";
import { getDB } from "./schema.js";
import { todayDate, nowIso } from "../utils/dates.js";
import { queueCloudWrite } from "./sync.js";
import { logItemChange } from "./history.js";
import { TIMES_FINISHED, AWARDS } from "../components/status-badge.js";
import { COMIC_FORMATS, comicKey, normalizeRanges } from "../utils/comics.js";

// Comics — the sixth media type (design_documents/Comics/media-tracker-comics-spec.md).
// One flat record per single issue or collected edition. The only media type
// with two sources: Metron (issues, and the trades Metron knows) and Open
// Library (trades). Records carry `source` + `external_id` and are keyed by
// `comic_key` = "<source>:<external_id>". A close clone of db/movies.js plus the
// comic fields and the user-curated `collects` list.

const STORE = "comics";
const MEDIA = "comic";

function uuid() {
	if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
}

export async function listComics() {
	const db = await getDB();
	return db.getAll(STORE);
}

export async function getComicById(id) {
	const db = await getDB();
	return db.get(STORE, id);
}

export async function getComicByKey(key) {
	if (!key) return undefined;
	const db = await getDB();
	return db.getFromIndex(STORE, "comic_key", String(key));
}

export function getComicBySource(source, externalId) {
	return getComicByKey(comicKey(source, String(externalId)));
}

export async function addComic({
	source,
	external_id,
	title,
	cover_url = null,
	format = "issue",
	series = null,
	number = null,
	publisher = null,
	cover_date = null,
	release_year = null,
	isbn = null,
	page_count = null,
	summary = null,
	story_titles = [],
	creators = [],
	reprints = [],
	gcd_id = null,
	engagement_status = defaultAddStatus(),
	ownership_status = defaultAddOwnership(),
	formats = [],
}) {
	const key = comicKey(source, String(external_id ?? ""));
	const existing = await getComicByKey(key);
	if (existing) return existing;

	const now = nowIso();
	const today = todayDate();
	const record = {
		id: uuid(),
		comic_key: key,
		source,
		external_id: String(external_id),
		title,
		cover_url,
		format: COMIC_FORMATS.includes(format) ? format : "other",
		series,
		number,
		publisher,
		cover_date,
		release_year,
		isbn,
		page_count,
		summary,
		story_titles,
		creators,
		// Collected editions: the issues they contain, entered by the user
		// ([{ series_id, series_label, ranges }]); Metron's own list is `reprints`.
		collects: [],
		reprints,
		gcd_id,
		media_type: MEDIA,
		engagement_status,
		times_finished: null,
		// Canonical column the app no longer uses (times_finished replaced it);
		// kept at 0 so the synced cloud row stays valid.
		playthrough_count: 0,
		date_first_completed: engagement_status === "done" ? today : null,
		last_engaged_date:
			engagement_status === "playing" ||
			engagement_status === "replaying" ||
			engagement_status === "done"
				? today
				: null,
		rating: null,
		ownership_status,
		formats,
		note: null,
		awards: [],
		// Forward-looking; no UI yet (friends are deferred).
		visibility: "friends",
		created_at: now,
		updated_at: now,
	};
	const db = await getDB();
	await db.add(STORE, record);
	await queueCloudWrite({ type: "create", mediaType: MEDIA, record });
	await logItemChange(MEDIA, null, record);
	return record;
}

export async function updateComic(id, patch) {
	const db = await getDB();
	const current = await db.get(STORE, id);
	if (!current) throw new Error(`Comic ${id} not found`);

	const next = { ...current, ...patch, updated_at: nowIso() };

	if (
		patch.engagement_status &&
		patch.engagement_status !== current.engagement_status
	) {
		const today = todayDate();
		if (
			patch.engagement_status === "playing" ||
			patch.engagement_status === "replaying"
		) {
			next.last_engaged_date = today;
		}
		if (patch.engagement_status === "done") {
			next.last_engaged_date = today;
			if (!current.date_first_completed) {
				next.date_first_completed = today;
			}
		}
	}

	await db.put(STORE, next);
	await queueCloudWrite({ type: "update", mediaType: MEDIA, record: next });
	await logItemChange(MEDIA, current, next);
	return next;
}

export const NOTE_MAX_LENGTH = 280;

export async function setComicNote(id, note) {
	// Keep it a note, not a review: trim and cap. Empty string clears it.
	const trimmed = (note ?? "").trim().slice(0, NOTE_MAX_LENGTH);
	return updateComic(id, { note: trimmed === "" ? null : trimmed });
}

export async function setComicFormats(id, list) {
	if (!Array.isArray(list)) throw new Error("formats must be an array");
	return updateComic(id, { formats: list });
}

// What the item is (issue / TPB / HC / omnibus / other). Open Library can't tell
// a hardcover from a paperback, so the user can correct it.
export async function setComicFormat(id, format) {
	if (!COMIC_FORMATS.includes(format)) throw new Error(`Invalid comic format: ${format}`);
	return updateComic(id, { format });
}

export async function setComicRating(id, rating) {
	if (
		rating !== null &&
		(!Number.isInteger(rating) || rating < 1 || rating > 5)
	) {
		throw new Error("Rating must be 1-5 or null");
	}
	return updateComic(id, { rating });
}

export async function setComicTimesFinished(id, value) {
	if (value !== null && !TIMES_FINISHED.some((b) => b.value === value)) {
		throw new Error(`Invalid times_finished bucket: ${value}`);
	}
	return updateComic(id, { times_finished: value });
}

export async function setComicAwards(id, list) {
	if (!Array.isArray(list)) throw new Error("awards must be an array");
	const valid = new Set(AWARDS.map((a) => a.value));
	const clean = list.filter((v) => valid.has(v));
	return updateComic(id, { awards: clean });
}

// Validate + canonicalise one "Collects…" entry: a series (Metron id when picked
// from Metron, null when typed by hand) and its issue ranges ("188–199, 201").
// Returns the clean entry or throws with a message fit for a toast.
export function cleanCollectsEntry(entry) {
	const label = String(entry?.series_label ?? "").trim();
	if (!label) throw new Error("Pick or type a series.");
	const ranges = normalizeRanges(entry?.ranges);
	if (!ranges) throw new Error(`Can't read the issue numbers "${entry?.ranges ?? ""}".`);
	const sid = entry?.series_id;
	return {
		series_id: sid == null || sid === "" ? null : Number(sid),
		series_label: label,
		ranges,
	};
}

// Replace the whole "Collects…" list (the editor saves it as a unit). An entry
// for a series already listed is merged into it rather than duplicated.
export async function setComicCollects(id, list) {
	if (!Array.isArray(list)) throw new Error("collects must be an array");
	const merged = [];
	for (const raw of list) {
		const entry = cleanCollectsEntry(raw);
		const same = merged.find((m) =>
			entry.series_id != null
				? m.series_id === entry.series_id
				: m.series_id == null && m.series_label.toLowerCase() === entry.series_label.toLowerCase(),
		);
		if (same) same.ranges = normalizeRanges(`${same.ranges}, ${entry.ranges}`);
		else merged.push(entry);
	}
	return updateComic(id, { collects: merged });
}

export async function deleteComic(id) {
	const db = await getDB();
	// Read first so the cloud delete op can carry the source + external id.
	const current = await db.get(STORE, id);
	await db.delete(STORE, id);
	if (current) {
		await queueCloudWrite({ type: "delete", mediaType: MEDIA, record: current });
		await logItemChange(MEDIA, current, null);
	}
}

export async function deleteComicByKey(key) {
	const existing = await getComicByKey(key);
	if (!existing) return false;
	await deleteComic(existing.id);
	return true;
}
